import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import SimpleEditorHeader from '@/editor/lib/components/EditorHeader/SimpleEditorHeader';
import {
  TextField,
  Button,
  Autocomplete,
  TableCell,
  TableContainer,
  Table,
  TableRow,
  TableHead,
  Paper,
  TableBody,
} from '@mui/material';
import Container from '../../components/Container';
import { Api } from '../../services/Api';
import Styles from '../../assets/js/Styles';
import { Design } from '../../declarations/models/Design';
import { Status } from '../../declarations/models/Status';

export interface DesignEditorProps {
  designId?: number | null;
  ownerId?: number | null;
  siteId?: number | null;
  isGlobal?: boolean;
  onCloseEditor: () => void;
}

export const editorStyling = {
  maxHeight: '100%',
  overflowY: 'auto',
  padding: 2,
  borderRadius: 2,
  backgroundColor: Styles.Colors.FINDER_M24_PRIMARY_HEADER_BACKGROUND,
};

export type DesignTextFields = 'title' | 'css';

export const DesignEditor: FC<DesignEditorProps> = ({ designId, isGlobal, ownerId, siteId, onCloseEditor }) => {
  const { t: tComp } = useTranslation('components');
  const { t: tCommon } = useTranslation('common');
  const { enqueueSnackbar } = useSnackbar();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [design, setDesign] = useState<Design>();
  const [modifiedDesign, setModifiedDesign] = useState<Design>();

  useEffect(() => {
    if (designId) {
      const ctx = Api.getDesign(Number(designId || 0));
      ctx
        .fetchDirect(null)
        .catch((e) => console.error(e))
        .then((res) => {
          if (res) {
            setDesign(res);
            setModifiedDesign({
              ...res,
              skins: Array.isArray(res?.skins) ? [...res.skins] : [],
              themes: Array.isArray(res?.themes) ? [...res.themes] : [],
              layouts: Array.isArray(res?.layouts) ? [...res.layouts] : [],
            });
          }
        })
        .finally(ctx.abort);
    }
  }, [designId]);

  // Remove this entire useEffect:
  // useEffect(() => {
  //   if (modifiedDesign) {
  //     setModifiedDesign({
  //       ...modifiedDesign,
  //       skins: Array.isArray(modifiedDesign?.skins) ? [...modifiedDesign.skins] : [],
  //       themes: Array.isArray(modifiedDesign?.themes) ? [...modifiedDesign.themes] : [],
  //       layouts: Array.isArray(modifiedDesign?.layouts) ? [...modifiedDesign.layouts] : [],
  //     });
  //   }
  // }, [modifiedDesign]);

  // useEffect(() => setIsDirty(!isDeepEqual(modifiedDesign, design)), [modifiedDesign, design]);

  const handleImportDesign = async () => {
    if (modifiedDesign) {
      const ctx = Api.importDesign(modifiedDesign);
      await ctx
        .fetchDirect(null)
        .then((res) => {
          if (res) {
            setModifiedDesign({ ...modifiedDesign, skins: res.skins, themes: res.themes, layouts: res.layouts });
            enqueueSnackbar('Import vellykket. Du må lagre endringene', { variant: 'success' });
          }
        })
        .catch((e) =>
          enqueueSnackbar(tComp('EmployeeEditor.SaveFailed', { error: e.error || e }), { variant: 'error' }),
        );
    }
  };

  const handleSaveDesign = async () => {
    if (modifiedDesign) {
      const ctx = Api.saveDesign(modifiedDesign);
      await ctx
        .fetchDirect(null)
        .then((res) => {
          if (res) {
            setModifiedDesign({
              ...modifiedDesign,
              id: res.id,
              skins: res.skins,
              themes: res.themes,
              layouts: res.layouts,
            });
            enqueueSnackbar('Oppdatering vellykket', { variant: 'success' });
            setIsDirty(false);
          }
        })
        .catch((e) =>
          enqueueSnackbar(tComp('EmployeeEditor.SaveFailed', { error: e.error || e }), { variant: 'error' }),
        );
    }
  };

  const handleChangeStringValue = (attribute: DesignTextFields, value: string) => {
    setModifiedDesign({
      ...modifiedDesign,
      [attribute]: value,
      status: modifiedDesign?.status || Status.DRAFT,
      is_global: isGlobal,
      owner_id: modifiedDesign?.owner_id || ownerId || null,
      site_id: modifiedDesign?.site_id || siteId || null,
    } as Design);
    setIsDirty(true);
  };

  return (
    <Container fullHeight fullWidth column gap={0}>
      <SimpleEditorHeader
        model={design}
        title={design?.title ?? 'Nytt design'}
        onClose={onCloseEditor}
        onSave={handleSaveDesign}
        isDirty={isDirty}
      />
      <Container fullWidth top gap={6} sx={{ marginTop: '16px', height: 'calc(100% -  56px)' }}>
        <Container fullWidth fullHeight left column top gap={2} sx={editorStyling}>
          <Container fullWidth top gap={2} column>
            <TextField
              size='small'
              label='Tittel'
              fullWidth
              required
              value={modifiedDesign?.title ?? ''}
              onChange={(e) => handleChangeStringValue('title', e.target.value)}
            />
            <Container fullWidth top gap={2} left>
              <Autocomplete
                value={modifiedDesign?.status || 'draft'}
                getOptionLabel={(s) => tCommon(`Status.${s}`)}
                options={Object.values(Status)}
                onChange={(e, value) => {
                  if (modifiedDesign) {
                    setModifiedDesign({ ...modifiedDesign, status: (value as Status) || Status.DRAFT });
                    setIsDirty(true);
                  }
                }}
                sx={{ width: '50%' }}
                renderInput={(params) => <TextField {...params} label='Status' />}
              />

              <Button
                sx={{ width: '50%' }}
                variant='contained'
                color='secondary'
                onClick={handleImportDesign}
                disabled={!isDirty}>
                Importer skins/themes &raquo;&raquo;
              </Button>
            </Container>
            <Container fullWidth fullHeight sx={{ fontFamily: 'monospace' }}>
              <TextField
                multiline
                minRows={30}
                maxRows={30}
                size='small'
                label='CSS'
                fullWidth
                required
                value={modifiedDesign?.css ?? ''}
                onChange={(e) => handleChangeStringValue('css', e.target.value)}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: '#1e1e1e', // Dark background
                    color: '#f0f0f0', // Light text color
                  },
                  '& .MuiInputLabel-root': {
                    color: '#888', // Label color
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#444', // Border color
                  },
                }}
              />
            </Container>
          </Container>
        </Container>
        <Container fullWidth top column left>
          <Container fullWidth top column left>
            {modifiedDesign?.skins && modifiedDesign?.skins.length > 0 && (
              <Container fullWidth left mb={4} column>
                <strong>SKINS</strong>
                <Container fullWidth top left wrap>
                  {modifiedDesign && Array.isArray(modifiedDesign?.skins)
                    ? modifiedDesign.skins.map((skin) => (
                        <div
                          key={skin.class}
                          style={{
                            backgroundColor: skin.hex,
                            color: skin.txt,
                            padding: 10,
                            fontSize: '12px',
                            width: '200px',
                          }}>
                          <strong>{skin.title}</strong>
                          <br />
                          {skin.class}
                          <br />
                          <span>
                            {skin.hex} /<span style={{ backgroundColor: skin.rgb }}>{skin.rgb}</span>
                            <br />
                          </span>
                          Textcolor: {skin.txt}
                        </div>
                      ))
                    : null}
                </Container>
              </Container>
            )}

            {modifiedDesign?.themes && modifiedDesign?.themes.length > 0 && (
              <Container fullWidth left mb={4} column>
                <strong>THEMES / SIDETEMAER</strong>
                <Container fullWidth top left wrap>
                  {modifiedDesign && Array.isArray(modifiedDesign?.themes)
                    ? modifiedDesign.themes.map((skin) => (
                        <div
                          key={skin.class}
                          style={{
                            backgroundColor: skin.hex,
                            color: skin.txt,
                            padding: 10,
                            fontSize: '12px',
                            width: '200px',
                          }}>
                          <strong>{skin.title}</strong>
                          <br />
                          {skin.class}
                          <br />
                          <span>
                            {skin.hex} /<span style={{ backgroundColor: skin.rgb }}>{skin.rgb}</span>
                            <br />
                          </span>
                          Textcolor: {skin.txt}
                        </div>
                      ))
                    : null}
                </Container>
              </Container>
            )}
          </Container>
          <Container fullWidth top column left>
            <TableContainer component={Paper}>
              <Table className='compact-table' stickyHeader sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Stilsettet er i bruk på følgende nettsteder:</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {design && Array.isArray(design.used_in_sites) && design.used_in_sites.length > 0 ? (
                    design.used_in_sites.map((site) => (
                      <TableRow key={site.id}>
                        <TableCell>{site.name}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2}>No sites found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Container>
        </Container>
      </Container>
    </Container>
  );
};

export default DesignEditor;
